import { Utils } from '@genial/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { Control, useForm } from 'react-hook-form';
import { useRecoilValue } from 'recoil';

import { productsCartState } from '../../../../../../../store/batchTicket/atom';
import {
  AssetDefaultOrSelectedTypeMap,
  SubmitUpdateProps,
} from '../../../../../../../store/product/types';

import { FundsFormData, getValidationCreateSchema } from './constants';

export const useFundsAssetController = (
  offer: AssetDefaultOrSelectedTypeMap['funds'],
  submitUpdate?: (data: SubmitUpdateProps) => Promise<boolean>
) => {
  const minimumMovimentation = offer.minimumMovimentationNormalized;

  const [isFocused, setIsFocused] = useState(false);

  const fundOffersSelected = useRecoilValue(productsCartState).funds;

  const valueAppliedBasket = fundOffersSelected.items.findIndex(
    (item) => item.productIdNormalized === offer.productIdNormalized
  );

  const [valueApplied, setValueApplied] = useState(
    fundOffersSelected?.items[valueAppliedBasket]?.orderValue ||
      offer.orderValue ||
      minimumMovimentation
  );

  const valueAppliedFormatted = Utils.Mask.Money(valueApplied);
  const minimumMovimentationFormatted = Utils.Mask.Money(minimumMovimentation);

  const tableForm = useForm<FundsFormData>({
    mode: 'all',
    reValidateMode: 'onBlur',
    defaultValues: {
      createAppliedAmount: valueAppliedFormatted,
    },
    resolver: zodResolver(getValidationCreateSchema(minimumMovimentation)),
  });

  const handleInputFocus = () => {
    setIsFocused(true);
    if (tableForm.formState.errors?.createAppliedAmount) {
      tableForm.resetField('createAppliedAmount');
    }
  };

  const handleInputClear = () => {
    setIsFocused(false);
    tableForm.resetField('createAppliedAmount');
    tableForm.clearErrors('createAppliedAmount');
  };

  const handleInputBlur = async () => {
    const currentValue = tableForm.getValues('createAppliedAmount');
    const currentValueUnformatted = Utils.Unmask.Money(currentValue);

    const updateFormValues = () => {
      tableForm.setValue('createAppliedAmount', currentValue);
      setValueApplied(currentValueUnformatted);
    };

    if (
      currentValue !== minimumMovimentationFormatted &&
      currentValueUnformatted >= minimumMovimentation
    ) {
      if (submitUpdate) {
        const response = await submitUpdate({ amount: currentValueUnformatted });
        if (response) {
          updateFormValues();
        }
      } else {
        updateFormValues();
      }
    }

    if (isFocused) {
      setIsFocused(false);
    }
  };

  const helpTextWhenIsFocused = `Mínimo: ${Utils.Mask.Money(minimumMovimentation)}`;

  const handleSubmit = async ({ createAppliedAmount }: FundsFormData) => {
    const updateFormValues = () => {
      const createAppliedAmountUnformatted = Utils.Unmask.Money(createAppliedAmount);
      tableForm.setValue('createAppliedAmount', createAppliedAmount);
      setValueApplied(createAppliedAmountUnformatted);
    };

    if (submitUpdate) {
      const unformattedAmount = Utils.Unmask.Money(createAppliedAmount);
      const response = await submitUpdate({ amount: unformattedAmount });

      if (response) {
        updateFormValues();
      }
    } else {
      updateFormValues();
    }

    setIsFocused(false);
  };

  const productsDetailText = 'Veja mais detalhes\nsobre este produto';

  return {
    handleInputFocus,
    handleInputClear,
    isFocused,
    handleInputBlur,
    handleSubmit: tableForm.handleSubmit(handleSubmit),
    control: tableForm.control as unknown as Control,
    hasError: tableForm.formState.errors?.createAppliedAmount,
    helpText: helpTextWhenIsFocused,
    productsDetailText,
    currentValue: valueApplied,
    currentValueFormatted: valueAppliedFormatted,
  };
};

export type FundsAssetController = ReturnType<typeof useFundsAssetController>;
